import { Spin } from "antd";
import { Link } from "react-router-dom";
import { LoadingOutlined } from "@ant-design/icons";

/**
 * @param {string} [to] - A route: renders a `<Link>` with the same look, so a button that only
 *   navigates can be copied and opened in a new tab. Use `onClick` for anything else. Ignored
 *   while `disabled` or `loading`, which fall back to the inert `<button>`.
 */
const SecondaryButton = ({
  children,
  onClick,
  to,
  className = "",
  disabled = false,
  loading = false,
  type = "button",
  // AntD's name for the DOM `type`. Accepted rather than dropped into `...rest`, because this
  // codebase is AntD end to end and `htmlType` is what everyone reaches for — and dropping it
  // put `htmltype="submit"` on the DOM while the button stayed `type="button"`, which is how
  // nine CRM forms ended up with a submit button that could not submit anything.
  htmlType,
  ...rest
}) => {
  const handleClick = (e) => {
    if (!disabled && !loading && onClick) {
      onClick(e);
    }
  };

  const baseStyles =
    "h-[44px] px-6 rounded-md text-[13px] font-medium leading-5 transition-all duration-300 ease-in-out inline-flex items-center justify-center gap-2 border-2";

  // Same rename as PrimaryButton: `brand` is the #3956ea these hexes spelled out.
  const colorStyles =
    disabled || loading
      ? "border-brand text-brand bg-transparent opacity-50 cursor-not-allowed"
      : "border-brand text-brand bg-transparent hover:bg-brand hover:text-surface cursor-pointer";

  const isLink = Boolean(to) && !disabled && !loading;
  const Control = isLink ? Link : "button";
  const controlProps = isLink
    ? { to }
    : {
        type: htmlType || type,
        onClick: handleClick,
        disabled: disabled || loading,
      };

  return (
    <Control
      className={`${baseStyles} ${colorStyles} ${className}`}
      {...controlProps}
      {...rest}
    >
      {loading && (
        <Spin
          indicator={
            <LoadingOutlined
              style={{ fontSize: 16, color: "rgb(var(--color-brand))" }}
              spin
            />
          }
        />
      )}
      {children}
    </Control>
  );
};

export default SecondaryButton;
